/**
 * Rigged Character Renderer
 *
 * Renders user-uploaded rigged characters on the PixiJS canvas.
 * Supports bone animations, drag/drop positioning, and scaling.
 */

import React, { useEffect } from 'react';
import {
  Application,
  Sprite,
  Graphics,
  Container,
  Assets,
  Texture,
} from 'pixi.js';
import { TimelineState } from '@xzdarcy/react-timeline-editor';
import gsap from 'gsap';
import {
  Bone,
  BoneKeyframe,
  CharacterRig,
  RigAction,
} from '../../types/RiggingTypes';
import {
  AssetType,
  Position,
  Layer,
  RiggedCharacterRef,
} from '../../types/typing';
import { ActionRegistry } from '../../services/ActionRegistry';
import {
  startRecording,
  stopRecording,
  updateCircleVisibility,
} from './utils/recordAnimation';
import { drawBorder } from './utils/drawBorder';
import { isBackgroundRecordingApp } from './utils/backgroundRecordingState';

type Point = { x: number; y: number };

type SegmentRuntime = {
  sprite: Sprite;
  restPivot: Point;
  parentBoneName: string | null;
  animationBoneName: string;
};

type SegmentRigRuntime = {
  byBoneName: Map<string, SegmentRuntime>;
};

type AffineMatrix = {
  a: number;
  b: number;
  c: number;
  d: number;
  tx: number;
  ty: number;
};

const segmentRigRuntime = new WeakMap<RiggedCharacterRef, SegmentRigRuntime>();

const IDENTITY_MATRIX: AffineMatrix = {
  a: 1,
  b: 0,
  c: 0,
  d: 1,
  tx: 0,
  ty: 0,
};

const REMAINDER_CLEAR_PADDING_PX = 1;
const SEGMENT_MASK_PADDING_PX = 1;
const SEGMENT_BLEED_RATIO = 0.015;

function multiplyMatrices(
  left: AffineMatrix,
  right: AffineMatrix
): AffineMatrix {
  return {
    a: left.a * right.a + left.c * right.b,
    b: left.b * right.a + left.d * right.b,
    c: left.a * right.c + left.c * right.d,
    d: left.b * right.c + left.d * right.d,
    tx: left.a * right.tx + left.c * right.ty + left.tx,
    ty: left.b * right.tx + left.d * right.ty + left.ty,
  };
}

function createTranslationMatrix(x: number, y: number): AffineMatrix {
  return {
    a: 1,
    b: 0,
    c: 0,
    d: 1,
    tx: x,
    ty: y,
  };
}

function createRotateAroundMatrix(angle: number, pivot: Point): AffineMatrix {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);

  return {
    a: cos,
    b: sin,
    c: -sin,
    d: cos,
    tx: pivot.x - pivot.x * cos + pivot.y * sin,
    ty: pivot.y - pivot.x * sin - pivot.y * cos,
  };
}

function applyMatrixToPoint(matrix: AffineMatrix, point: Point): Point {
  return {
    x: matrix.a * point.x + matrix.c * point.y + matrix.tx,
    y: matrix.b * point.x + matrix.d * point.y + matrix.ty,
  };
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Failed to load rig image: ${src}`));
    image.src = src;
  });
}

function clampNormalizedValue(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function clampNormalizedBounds(bounds: CharacterRig['segments'][number]['bounds']) {
  const x = clampNormalizedValue(bounds.x);
  const y = clampNormalizedValue(bounds.y);
  const maxX = clampNormalizedValue(bounds.x + bounds.width);
  const maxY = clampNormalizedValue(bounds.y + bounds.height);

  return {
    x,
    y,
    width: Math.max(0, maxX - x),
    height: Math.max(0, maxY - y),
  };
}

function createSyntheticPelvisSegment(
  rig: CharacterRig,
  segments: CharacterRig['segments']
): CharacterRig['segments'][number] | null {
  if (
    rig.bones.some((bone) => /pelvis|hips?/i.test(bone.name)) ||
    segments.some((segment) => segment.boneName === 'pelvis')
  ) {
    return null;
  }

  const torsoBone = rig.bones.find((bone) => /(torso|body|chest|spine)/i.test(bone.name));
  const leftUpperLegBone = rig.bones.find(
    (bone) => /left/i.test(bone.name) && /(upper_leg|thigh|leg)/i.test(bone.name)
  );
  const rightUpperLegBone = rig.bones.find(
    (bone) => /right/i.test(bone.name) && /(upper_leg|thigh|leg)/i.test(bone.name)
  );

  if (!torsoBone || !leftUpperLegBone || !rightUpperLegBone) {
    return null;
  }

  const torsoStartJoint = rig.joints.find((joint) => joint.id === torsoBone.startJointId);
  const torsoEndJoint = rig.joints.find((joint) => joint.id === torsoBone.endJointId);
  const leftHipJoint = rig.joints.find(
    (joint) => joint.id === leftUpperLegBone.startJointId
  );
  const rightHipJoint = rig.joints.find(
    (joint) => joint.id === rightUpperLegBone.startJointId
  );
  const leftKneeJoint = rig.joints.find(
    (joint) => joint.id === leftUpperLegBone.endJointId
  );
  const rightKneeJoint = rig.joints.find(
    (joint) => joint.id === rightUpperLegBone.endJointId
  );

  if (
    !torsoStartJoint ||
    !torsoEndJoint ||
    !leftHipJoint ||
    !rightHipJoint ||
    !leftKneeJoint ||
    !rightKneeJoint
  ) {
    return null;
  }

  const lowerTorsoJoint =
    torsoStartJoint.position.y > torsoEndJoint.position.y
      ? torsoStartJoint
      : torsoEndJoint;
  const averageHipX = (leftHipJoint.position.x + rightHipJoint.position.x) / 2;
  const averageHipY = (leftHipJoint.position.y + rightHipJoint.position.y) / 2;
  const hipSpread = Math.abs(leftHipJoint.position.x - rightHipJoint.position.x);
  const upperLegReach = Math.max(
    Math.abs(leftKneeJoint.position.y - leftHipJoint.position.y),
    Math.abs(rightKneeJoint.position.y - rightHipJoint.position.y)
  );
  const bounds = clampNormalizedBounds({
    x:
      Math.min(leftHipJoint.position.x, rightHipJoint.position.x) -
      Math.max(0.035, hipSpread * 0.2),
    y:
      Math.min(lowerTorsoJoint.position.y, averageHipY) -
      Math.max(0.04, upperLegReach * 0.15),
    width:
      hipSpread + Math.max(0.07, hipSpread * 0.4),
    height:
      Math.max(leftHipJoint.position.y, rightHipJoint.position.y) -
      Math.min(lowerTorsoJoint.position.y, averageHipY) +
      Math.max(0.08, upperLegReach * 0.22),
  });

  if (bounds.width < 0.04 || bounds.height < 0.04) {
    return null;
  }

  return {
    boneId: torsoBone.id,
    boneName: 'pelvis',
    animationBoneName: torsoBone.name,
    bounds,
    pivot: {
      x: averageHipX,
      y: Math.max(lowerTorsoJoint.position.y, averageHipY - upperLegReach * 0.08),
    },
    rotation: 0,
  };
}

function getRenderableSegments(rig: CharacterRig): CharacterRig['segments'] {
  const baseSegments = rig.segments || [];
  const pelvisSegment = createSyntheticPelvisSegment(rig, baseSegments);

  return pelvisSegment ? [...baseSegments, pelvisSegment] : baseSegments;
}

function getSegmentBleedPaddingPx(rig: CharacterRig): number {
  return Math.max(
    2,
    Math.round(Math.min(rig.imageWidth, rig.imageHeight) * SEGMENT_BLEED_RATIO)
  );
}

function getSegmentSourceRect(
  rig: CharacterRig,
  segment: CharacterRig['segments'][number]
) {
  const bleedPadding = getSegmentBleedPaddingPx(rig);
  const totalPadding = REMAINDER_CLEAR_PADDING_PX + bleedPadding;

  const x = Math.max(
    0,
    Math.floor(segment.bounds.x * rig.imageWidth) - totalPadding
  );
  const y = Math.max(
    0,
    Math.floor(segment.bounds.y * rig.imageHeight) - totalPadding
  );
  const maxX = Math.min(
    rig.imageWidth,
    Math.ceil((segment.bounds.x + segment.bounds.width) * rig.imageWidth) +
      totalPadding
  );
  const maxY = Math.min(
    rig.imageHeight,
    Math.ceil((segment.bounds.y + segment.bounds.height) * rig.imageHeight) +
      totalPadding
  );

  return {
    x,
    y,
    width: Math.max(0, maxX - x),
    height: Math.max(0, maxY - y),
  };
}

function isLimbLikeSegment(segment: CharacterRig['segments'][number]): boolean {
  return /(upper_arm|forearm|lower_arm|hand|upper_leg|thigh|lower_leg|calf|shin|foot|shoe)/i.test(
    segment.boneName
  );
}

function getSegmentMaskData(
  rig: CharacterRig,
  segment: CharacterRig['segments'][number]
): {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
  angle: number;
} | null {
  if (!isLimbLikeSegment(segment)) {
    return null;
  }

  const bone = rig.bones.find((candidate) => candidate.id === segment.boneId);
  if (!bone) return null;

  const startJoint = rig.joints.find((joint) => joint.id === bone.startJointId);
  const endJoint = rig.joints.find((joint) => joint.id === bone.endJointId);
  if (!startJoint || !endJoint) return null;

  const startX = startJoint.position.x * rig.imageWidth;
  const startY = startJoint.position.y * rig.imageHeight;
  const endX = endJoint.position.x * rig.imageWidth;
  const endY = endJoint.position.y * rig.imageHeight;
  const dx = endX - startX;
  const dy = endY - startY;
  const boneLength = Math.sqrt(dx * dx + dy * dy);
  const bleedPadding = getSegmentBleedPaddingPx(rig);

  return {
    centerX: (startX + endX) / 2,
    centerY: (startY + endY) / 2,
    width:
      boneLength +
      rig.imageWidth * 0.04 +
      (SEGMENT_MASK_PADDING_PX + bleedPadding) * 2,
    height:
      (bone.width || 0.12) * rig.imageWidth +
      (SEGMENT_MASK_PADDING_PX + bleedPadding) * 2,
    angle: Math.atan2(dy, dx),
  };
}

function withSegmentMaskPath(
  context: CanvasRenderingContext2D,
  rig: CharacterRig,
  segment: CharacterRig['segments'][number],
  draw: () => void
) {
  const mask = getSegmentMaskData(rig, segment);

  if (!mask) {
    const rect = getSegmentSourceRect(rig, segment);
    if (rect.width <= 0 || rect.height <= 0) return;

    context.save();
    context.beginPath();
    context.rect(rect.x, rect.y, rect.width, rect.height);
    context.clip();
    draw();
    context.restore();
    return;
  }

  context.save();
  context.translate(mask.centerX, mask.centerY);
  context.rotate(mask.angle);
  context.beginPath();
  context.rect(-mask.width / 2, -mask.height / 2, mask.width, mask.height);
  context.clip();
  context.rotate(-mask.angle);
  context.translate(-mask.centerX, -mask.centerY);
  draw();
  context.restore();
}

function createSegmentTexture(
  image: HTMLImageElement,
  rig: CharacterRig,
  segment: CharacterRig['segments'][number]
): Texture | null {
  const sourceRect = getSegmentSourceRect(rig, segment);
  if (sourceRect.width <= 0 || sourceRect.height <= 0) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = sourceRect.width;
  canvas.height = sourceRect.height;

  const context = canvas.getContext('2d');
  if (!context) return null;

  context.save();
  context.translate(-sourceRect.x, -sourceRect.y);
  withSegmentMaskPath(context, rig, segment, () => {
    context.drawImage(image, 0, 0, rig.imageWidth, rig.imageHeight);
  });
  context.restore();

  return Texture.from(canvas);
}

async function createRemainderTexture(
  rig: CharacterRig,
  segments: CharacterRig['segments']
): Promise<Texture | null> {
  if (
    typeof document === 'undefined' ||
    rig.imageWidth <= 0 ||
    rig.imageHeight <= 0 ||
    segments.length === 0
  ) {
    return null;
  }

  const image = await loadImageElement(rig.imageUrl);
  const canvas = document.createElement('canvas');
  canvas.width = rig.imageWidth;
  canvas.height = rig.imageHeight;

  const context = canvas.getContext('2d');
  if (!context) return null;

  context.drawImage(image, 0, 0, rig.imageWidth, rig.imageHeight);

  segments.forEach((segment) => {
    withSegmentMaskPath(context, rig, segment, () => {
      context.clearRect(0, 0, rig.imageWidth, rig.imageHeight);
    });
  });

  return Texture.from(canvas);
}

function getDistanceSquared(a: Point, b: Point): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return dx * dx + dy * dy;
}

function isPointInsideBounds(
  point: Point,
  bounds: { x: number; y: number; width: number; height: number },
  padding = 0
): boolean {
  return (
    point.x >= bounds.x - padding &&
    point.x <= bounds.x + bounds.width + padding &&
    point.y >= bounds.y - padding &&
    point.y <= bounds.y + bounds.height + padding
  );
}

function getBoneRank(boneName: string): number {
  if (/(torso|body|chest|spine|pelvis|hip)/i.test(boneName)) return 0;
  if (/(head|neck)/i.test(boneName)) return 1;
  if (/(upper_arm|upper_leg|thigh|leg)/i.test(boneName)) return 2;
  if (/(forearm|lower_arm|lower_leg|calf|shin)/i.test(boneName)) return 3;
  if (/(hand|foot|shoe)/i.test(boneName)) return 4;
  return 10;
}

function getBoneJointIds(bone: CharacterRig['bones'][number]): string[] {
  return [bone.startJointId, bone.endJointId];
}

function getSharedJointId(
  first: CharacterRig['bones'][number],
  second: CharacterRig['bones'][number]
): string | null {
  const firstJointIds = new Set(getBoneJointIds(first));
  const sharedJointId = getBoneJointIds(second).find((jointId) =>
    firstJointIds.has(jointId)
  );
  return sharedJointId ?? null;
}

function getClosestJointIdToBone(
  rig: CharacterRig,
  sourceBone: CharacterRig['bones'][number],
  referenceBone: CharacterRig['bones'][number]
): string {
  const sourceJoints = getBoneJointIds(sourceBone)
    .map((jointId) => rig.joints.find((joint) => joint.id === jointId))
    .filter((joint): joint is NonNullable<typeof joint> => Boolean(joint));

  const referenceJoints = getBoneJointIds(referenceBone)
    .map((jointId) => rig.joints.find((joint) => joint.id === jointId))
    .filter((joint): joint is NonNullable<typeof joint> => Boolean(joint));

  let closestJointId = sourceBone.startJointId;
  let closestDistance = Number.POSITIVE_INFINITY;

  sourceJoints.forEach((sourceJoint) => {
    referenceJoints.forEach((referenceJoint) => {
      const distance = getDistanceSquared(
        sourceJoint.position,
        referenceJoint.position
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestJointId = sourceJoint.id;
      }
    });
  });

  return closestJointId;
}

function getSegmentPivotPoint(rig: CharacterRig, boneId: string): Point | null {
  const bone = rig.bones.find((candidate) => candidate.id === boneId);
  if (!bone) return null;

  const parentBoneName = resolveParentBoneName(rig, boneId);
  const parentBone = parentBoneName
    ? rig.bones.find((candidate) => candidate.name === parentBoneName)
    : null;
  const sharedJointId = parentBone ? getSharedJointId(parentBone, bone) : null;
  const pivotJointId = parentBone
    ? sharedJointId || getClosestJointIdToBone(rig, bone, parentBone)
    : bone.startJointId;
  const pivotJoint = rig.joints.find((joint) => joint.id === pivotJointId);

  return pivotJoint?.position ?? null;
}

function getMatrixRotation(matrix: AffineMatrix): number {
  return Math.atan2(matrix.b, matrix.a);
}

function interpolateValue(start: number, end: number, progress: number): number {
  return start + (end - start) * progress;
}

function getBonePoseAtTime(
  keyframes: BoneKeyframe[],
  normalizedTime: number
): { rotation: number; translation: Point } {
  if (keyframes.length === 0) {
    return { rotation: 0, translation: { x: 0, y: 0 } };
  }

  if (normalizedTime <= keyframes[0].time) {
    return {
      rotation: keyframes[0].rotation,
      translation: keyframes[0].translation,
    };
  }

  const lastKeyframe = keyframes[keyframes.length - 1];
  if (normalizedTime >= lastKeyframe.time) {
    return {
      rotation: lastKeyframe.rotation,
      translation: lastKeyframe.translation,
    };
  }

  for (let index = 1; index < keyframes.length; index += 1) {
    const currentKeyframe = keyframes[index];
    if (normalizedTime > currentKeyframe.time) continue;

    const previousKeyframe = keyframes[index - 1];
    const span = currentKeyframe.time - previousKeyframe.time || 1;
    const progress = (normalizedTime - previousKeyframe.time) / span;

    return {
      rotation: interpolateValue(
        previousKeyframe.rotation,
        currentKeyframe.rotation,
        progress
      ),
      translation: {
        x: interpolateValue(
          previousKeyframe.translation.x,
          currentKeyframe.translation.x,
          progress
        ),
        y: interpolateValue(
          previousKeyframe.translation.y,
          currentKeyframe.translation.y,
          progress
        ),
      },
    };
  }

  return {
    rotation: lastKeyframe.rotation,
    translation: lastKeyframe.translation,
  };
}

function resetSegmentSprites(charRef: RiggedCharacterRef) {
  const runtime = segmentRigRuntime.get(charRef);
  if (!runtime) return;

  runtime.byBoneName.forEach(({ sprite, restPivot }) => {
    sprite.position.set(restPivot.x, restPivot.y);
    sprite.rotation = 0;
  });
}

function applySegmentActionPose(
  charRef: RiggedCharacterRef,
  action: RigAction,
  normalizedTime: number
) {
  const runtime = segmentRigRuntime.get(charRef);
  if (!runtime) return;

  const animationByBoneName = new Map(
    action.boneAnimations.map((animation) => [animation.boneName, animation])
  );
  const computedMatrices = new Map<string, AffineMatrix>();
  const activeBoneStack = new Set<string>();

  const computeWorldMatrix = (boneName: string): AffineMatrix => {
    const cached = computedMatrices.get(boneName);
    if (cached) return cached;

    const segment = runtime.byBoneName.get(boneName);
    if (!segment) return IDENTITY_MATRIX;

    if (activeBoneStack.has(boneName)) {
      console.warn(
        `Detected cyclic rig hierarchy while computing pose for bone "${boneName}".`
      );
      return IDENTITY_MATRIX;
    }

    activeBoneStack.add(boneName);

    let parentMatrix = IDENTITY_MATRIX;

    try {
      if (segment.parentBoneName) {
        if (segment.parentBoneName === boneName) {
          console.warn(
            `Bone "${boneName}" was resolved as its own parent. Falling back to root transform.`
          );
        } else {
          parentMatrix = computeWorldMatrix(segment.parentBoneName);
        }
      }

      const animation = animationByBoneName.get(segment.animationBoneName);
      const pose = animation
        ? getBonePoseAtTime(animation.keyframes, normalizedTime)
        : { rotation: 0, translation: { x: 0, y: 0 } };

      const localRotation = createRotateAroundMatrix(
        (pose.rotation * Math.PI) / 180,
        segment.restPivot
      );
      const localTranslation = createTranslationMatrix(
        pose.translation.x,
        pose.translation.y
      );
      const localMatrix = multiplyMatrices(localTranslation, localRotation);
      const worldMatrix = multiplyMatrices(parentMatrix, localMatrix);

      computedMatrices.set(boneName, worldMatrix);
      return worldMatrix;
    } finally {
      activeBoneStack.delete(boneName);
    }
  };

  runtime.byBoneName.forEach((segment, boneName) => {
    const worldMatrix = computeWorldMatrix(boneName);
    const animatedPivot = applyMatrixToPoint(worldMatrix, segment.restPivot);

    const sprite = segment.sprite;
    if (
      !sprite ||
      !sprite.position ||
      typeof sprite.position.set !== 'function'
    ) {
      return;
    }

    sprite.position.set(animatedPivot.x, animatedPivot.y);
    sprite.rotation = getMatrixRotation(worldMatrix);
  });
}

export function syncRiggedActionPose(
  charRef: RiggedCharacterRef,
  normalizedTime: number
) {
  if (!charRef.currentAction && charRef.actionId) {
    charRef.currentAction =
      ActionRegistry.getActionById(charRef.actionId) ?? null;
  }

  if (!charRef.currentAction) {
    resetSegmentSprites(charRef);
    return;
  }

  const clampedTime = Math.max(0, Math.min(1, normalizedTime));
  applySegmentActionPose(charRef, charRef.currentAction, clampedTime);
}

function resolveParentBoneName(rig: CharacterRig, boneId: string): string | null {
  const bone = rig.bones.find((candidate) => candidate.id === boneId);
  if (!bone) return null;

  const parentBone = rig.bones.find(
    (candidate) =>
      candidate.id !== bone.id && candidate.endJointId === bone.startJointId
  );
  if (parentBone) return parentBone.name;

  const startJoint = rig.joints.find((joint) => joint.id === bone.startJointId);
  if (!startJoint) return null;

  if (startJoint.parentId) {
    const parentFromJoint = rig.bones.find(
      (candidate) =>
        candidate.id !== bone.id && candidate.endJointId === startJoint.parentId
    );
    if (parentFromJoint) return parentFromJoint.name;
  }

  const torsoCandidates: Bone[] = rig.bones.filter((candidate) =>
    /(torso|body|chest|spine|pelvis|hip|neck)/i.test(candidate.name)
  );

  // Branch-style rigs often place shoulder/hip joints on the body area rather
  // than directly on the torso centerline bone. If a limb/root body part starts
  // inside a torso-like segment, inherit that body bone while keeping the
  // limb's own shoulder/hip pivot.
  if (/(head|upper_arm|upper_leg|thigh|leg)/i.test(bone.name)) {
    const containingTorsoCandidate = torsoCandidates.find((candidate) => {
      if (candidate.id === bone.id) return false;

      const candidateSegment = rig.segments.find(
        (segment) => segment.boneId === candidate.id
      );
      return (
        candidateSegment &&
        isPointInsideBounds(startJoint.position, candidateSegment.bounds, 0.03)
      );
    });

    if (containingTorsoCandidate) {
      return containingTorsoCandidate.name;
    }

    let closestCandidate: Bone | null = null;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const candidate of torsoCandidates) {
      if (candidate.id === bone.id) continue;

      const candidateStart = rig.joints.find(
        (joint) => joint.id === candidate.startJointId
      );
      const candidateEnd = rig.joints.find(
        (joint) => joint.id === candidate.endJointId
      );
      if (!candidateStart || !candidateEnd) continue;

      const distance = Math.min(
        getDistanceSquared(startJoint.position, candidateStart.position),
        getDistanceSquared(startJoint.position, candidateEnd.position)
      );

      if (distance < closestDistance) {
        closestDistance = distance;
        closestCandidate = candidate;
      }
    }

    if (closestCandidate) return closestCandidate.name;
  }

  // If a limb bone was authored in reverse, fall back to any connected bone
  // that looks more proximal in the body hierarchy.
  const connectedCandidates = rig.bones.filter((candidate) => {
    if (candidate.id === bone.id) return false;
    return getSharedJointId(candidate, bone) !== null;
  });

  const currentRank = getBoneRank(bone.name);
  const proximalCandidate = connectedCandidates
    .filter((candidate) => getBoneRank(candidate.name) < currentRank)
    .sort((left, right) => getBoneRank(left.name) - getBoneRank(right.name))[0];

  if (proximalCandidate) {
    return proximalCandidate.name;
  }

  return null;
}

interface RiggedCharacterRendererProps {
  app: Application | null;
  riggedCharactersRef: React.MutableRefObject<Map<string, RiggedCharacterRef>>;
  riggedCharacters: Array<{
    id: string;
    rig: CharacterRig;
    position: Position;
    scale: number;
    actionId?: string;
  }>;
  width: number;
  height: number;
  layers: Layer[] | undefined;
  onUpdateRiggedCharacter: (
    id: string,
    properties: {
      position?: Position;
      scale?: number;
      pathPoints?: Position[];
    }
  ) => void;
  onAssetSelection: (id: string, assetType: AssetType) => void;
  timelineState?: React.MutableRefObject<TimelineState | null>;
  // Recording props (values, refs are created internally)
  isRecording?: boolean;
  recordingStartTime?: number | null;
  selectedMenu?: string | null;
}

const RiggedCharacterRenderer: React.FC<RiggedCharacterRendererProps> = ({
  app,
  riggedCharactersRef,
  riggedCharacters,
  width,
  height,
  layers,
  onUpdateRiggedCharacter,
  onAssetSelection,
  timelineState,
  isRecording,
  recordingStartTime,
  selectedMenu,
}) => {
  // Create refs from prop values (to access in event handlers)
  const isRecordingRef = React.useRef<boolean | null | undefined>(null);
  const recordingStartTimeRef = React.useRef<number | null>(null);
  const selectedMenuRef = React.useRef<string | null>(null);

  // Keep refs in sync with props
  React.useEffect(() => {
    isRecordingRef.current = isRecording;
  }, [isRecording]);

  React.useEffect(() => {
    recordingStartTimeRef.current = recordingStartTime ?? null;
  }, [recordingStartTime]);

  React.useEffect(() => {
    selectedMenuRef.current = selectedMenu ?? null;
  }, [selectedMenu]);

  useEffect(() => {
    riggedCharactersRef.current.forEach((charRef) => {
      updateCircleVisibility(charRef.pathCircles, selectedMenuRef);
    });
  }, [selectedMenu]);

  useEffect(() => {
    if (!app || !riggedCharacters) return;

    const setupRiggedCharacters = async () => {
      for (const charData of riggedCharacters) {
        if (!riggedCharactersRef.current.has(charData.id)) {
          await setupRiggedCharacter(charData);
        } else {
          // Update existing character
          const existing = riggedCharactersRef.current.get(charData.id);
          if (existing) {
            const isStaleRef =
              !existing.container ||
              existing.container.destroyed ||
              existing.container.parent !== app.stage;

            if (isStaleRef) {
              existing.destroy?.();
              riggedCharactersRef.current.delete(charData.id);
              await setupRiggedCharacter(charData);
              continue;
            }

            existing.container.position.set(
              charData.position.x,
              charData.position.y
            );
            existing.container.scale.set(charData.scale);
            existing.position = charData.position;
            existing.scale = charData.scale;
          }
        }
      }

      // Cleanup removed characters
      const currentIds = new Set(riggedCharacters.map((c) => c.id));
      riggedCharactersRef.current.forEach((ref, id) => {
        if (!currentIds.has(id)) {
          ref.destroy();
          riggedCharactersRef.current.delete(id);
        }
      });
    };

    setupRiggedCharacters();
  }, [app, riggedCharacters, riggedCharactersRef, width, height]);

  useEffect(() => {
    if (!app) return;

    return () => {
      if (isBackgroundRecordingApp(app)) {
        return;
      }

      riggedCharactersRef.current.forEach((ref) => {
        ref.destroy?.();
      });
      riggedCharactersRef.current.clear();
    };
  }, [app, riggedCharactersRef]);

  // Sync GSAP Animation with Global Timeline
  useEffect(() => {
    if (!timelineState?.current) return;
    const engine = timelineState.current;

    const syncAnimation = ({ time }: { time: number }) => {
      riggedCharactersRef.current.forEach((char) => {
        if (char.timeline) {
          // Start or pause needs strict management
          if (!char.timeline.paused()) {
            char.timeline.pause();
          }

          const duration = char.timeline.duration();
          if (duration > 0) {
            // Keep export-time scrubbing in lockstep with the visible segment pose.
            const relativeTime = Math.max(0, time - (char.startTime || 0));
            const animationTime = relativeTime % duration;
            const progress = animationTime / duration;

            char.timeline.seek(animationTime);

            if (char.segmentSprites && char.segmentSprites.size > 0) {
              syncRiggedActionPose(char, progress);
            }
          }
        }
      });
    };

    engine.listener.on('setTimeByTick', syncAnimation);
    engine.listener.on('afterSetTime', syncAnimation);

    return () => {
      // Clean up listeners handled by engine.listener.offAll() in parent usually,
      // but specific cleanup isn't exposed easily on this library version effectively.
      // Relying on component unmount behavior or weak refs.
    };
  }, [timelineState]);

  useEffect(() => {
    const stage = app?.stage;
    if (!stage) return;

    const onStagePointerDown = () => {
      // This runs if propagation wasn't stopped (i.e., clicked background)
      let needsUpdate = false;
      riggedCharactersRef.current.forEach((ref) => {
        if (ref.isSelected) {
          ref.isSelected = false;
          if (ref.borderGraphics) {
            ref.borderGraphics.visible = false;
          }
          needsUpdate = true;
        }
      });

      if (needsUpdate) {
        onAssetSelection('', AssetType.NONE);
      }
    };

    // Use a low priority or ensuring it runs after others?
    // Actually standard event bubbling/capturing in Pixi:
    // Events start at root (stage) for capture, down to target, then bubble up.
    // .on() usually listens to bubble phase or just "at target".
    // If character handles 'pointerdown' and calls stopPropagation(), the stage listener WON'T fire if it's treated as a bubble parent.
    // PixiJS 7+ events behave like DOM events. Stage is the root.
    // Clicking a child (character) triggers child handler. bubbling triggers stage handler.
    // If child calls stopPropagation(), bubbling stops, so stage handler won't fire.
    // Perfect.

    stage.on('pointerdown', onStagePointerDown);

    return () => {
      stage.off('pointerdown', onStagePointerDown);
    };
  }, [app, onAssetSelection]);

  const setupRiggedCharacter = async (charData: {
    id: string;
    rig: CharacterRig;
    position: Position;
    scale: number;
    actionId?: string;
  }) => {
    if (!app?.stage) return;
    try {
      app.stage.sortableChildren = true;

      // Create container for the character
      const container = new Container();
      container.eventMode = 'static';
      container.cursor = 'pointer';
      container.zIndex = 100; // High zIndex to ensure visibility

      // Load the image as a sprite
      // Use Texture.from for robust Data URI handling
      const texture = await Assets.load(charData.rig.imageUrl).catch((err) => {
        console.warn('Assets.load failed, trying Texture.from', err);
        return Sprite.from(charData.rig.imageUrl).texture;
      });

      const sprite = new Sprite(texture);

      // Center the sprite anchor
      sprite.anchor.set(0.5, 0.5);

      // Scale sprite to reasonable size (max 200px)
      const maxSize = 200;
      const spriteScale = Math.min(
        maxSize / sprite.width,
        maxSize / sprite.height,
        1
      );
      sprite.scale.set(spriteScale);

      container.addChild(sprite);

      // Create graphics for bones (for visual feedback)
      const boneGraphics = new Graphics();
      boneGraphics.visible = false; // Hide bones by default
      container.addChild(boneGraphics);

      // Draw the bone structure
      const spriteWidth = sprite.width;
      const spriteHeight = sprite.height;
      drawBones(boneGraphics, charData.rig, spriteWidth, spriteHeight);

      // Create segment sprites from rig segments (if available)
      const segmentSprites = new Map<string, Sprite>();
      const segmentRuntimeByBoneName = new Map<string, SegmentRuntime>();
      const segments = getRenderableSegments(charData.rig);

      if (segments.length > 0) {
        const sourceImage = await loadImageElement(charData.rig.imageUrl).catch(
          (error) => {
            console.warn('Failed to load source image for rig segments', error);
            return null;
          }
        );

        if (!sourceImage) {
          if (!app.stage) {
            container.destroy({ children: true });
            return;
          }

          app.stage.addChild(container);

          const riggedCharRef: RiggedCharacterRef = {
            id: charData.id,
            rig: charData.rig,
            container,
            sprite,
            boneGraphics,
            position: charData.position,
            scale: charData.scale,
            isDragging: false,
            isSelected: false,
            currentAction: null,
            actionId: charData.actionId,
            timeline: null,
            startTime: 0,
            pathCircles: [],
            pathPoints: [],
            borderGraphics: null,
            segmentSprites,
            destroy: () => {
              container.off('pointerdown');
              container.off('pointerover');
              container.off('pointerout');
              container.off('pointermove');
              container.off('pointerup');
              container.off('pointerupoutside');
              container.destroy({ children: true });
              if (riggedCharRef.timeline) {
                riggedCharRef.timeline.kill();
              }
              segmentRigRuntime.delete(riggedCharRef);
            },
          };

          riggedCharactersRef.current.set(charData.id, riggedCharRef);
          segmentRigRuntime.set(riggedCharRef, {
            byBoneName: segmentRuntimeByBoneName,
          });
          setupInteractions(riggedCharRef);

          if (charData.actionId) {
            playAction(riggedCharRef, charData.actionId);
          }

          return;
        }

        // Hide the main sprite since we'll use segment sprites instead
        sprite.visible = false;

        const remainderTexture = await createRemainderTexture(
          charData.rig,
          segments
        ).catch((error) => {
          console.warn('Failed to create remainder texture', error);
          return null;
        });

        if (remainderTexture) {
          const remainderSprite = new Sprite(remainderTexture);
          remainderSprite.anchor.set(0.5, 0.5);
          remainderSprite.scale.set(spriteScale);
          container.addChild(remainderSprite);
        }

        for (const segment of segments) {
          const pivotPoint = segment.animationBoneName
            ? segment.pivot
            : getSegmentPivotPoint(charData.rig, segment.boneId) || segment.pivot;

          const sourceRect = getSegmentSourceRect(charData.rig, segment);
          const segmentTexture = createSegmentTexture(
            sourceImage,
            charData.rig,
            segment
          );
          if (!segmentTexture || sourceRect.width <= 0 || sourceRect.height <= 0) {
            continue;
          }

          const segmentSprite = new Sprite(segmentTexture);

          // Scale the segment sprite to match the main sprite
          segmentSprite.scale.set(spriteScale);

          // Position the segment sprite at the pivot point
          segmentSprite.position.set(
            (pivotPoint.x - 0.5) * spriteWidth,
            (pivotPoint.y - 0.5) * spriteHeight
          );

          // Set anchor to the pivot point within the segment
          segmentSprite.anchor.set(
            (pivotPoint.x * charData.rig.imageWidth - sourceRect.x) /
              sourceRect.width,
            (pivotPoint.y * charData.rig.imageHeight - sourceRect.y) /
              sourceRect.height
          );

          container.addChild(segmentSprite);
          segmentSprites.set(segment.boneName, segmentSprite);
          segmentRuntimeByBoneName.set(segment.boneName, {
            sprite: segmentSprite,
            restPivot: {
              x: (pivotPoint.x - 0.5) * spriteWidth,
              y: (pivotPoint.y - 0.5) * spriteHeight,
            },
            animationBoneName: segment.animationBoneName || segment.boneName,
            parentBoneName: resolveParentBoneName(charData.rig, segment.boneId),
          });
        }
      }

      // Set initial position
      container.position.set(charData.position.x, charData.position.y);
      container.scale.set(charData.scale);

      if (!app.stage) {
        container.destroy({ children: true });
        return;
      }

      app.stage.addChild(container);

      // Create ref object with mesh deformation properties
      const riggedCharRef: RiggedCharacterRef = {
        id: charData.id,
        rig: charData.rig,
        container,
        sprite,
        boneGraphics,
        position: charData.position,
        scale: charData.scale,
        isDragging: false,
        isSelected: false,
        currentAction: null,
        actionId: charData.actionId,
        timeline: null,
        startTime: 0,
        pathCircles: [],
        pathPoints: [],
        borderGraphics: null,
        // Segment sprites for bone-based animation
        segmentSprites,

        destroy: () => {
          container.off('pointerdown');
          container.off('pointermove');
          container.off('pointerup');
          container.off('pointerupoutside');
          container.off('wheel');
          if (riggedCharRef.timeline) {
            riggedCharRef.timeline.kill();
          }
          segmentRigRuntime.delete(riggedCharRef);
          if (app?.stage) app.stage.removeChild(container);
          container.destroy({ children: true });
        },
      };

      riggedCharactersRef.current.set(charData.id, riggedCharRef);
      segmentRigRuntime.set(riggedCharRef, {
        byBoneName: segmentRuntimeByBoneName,
      });

      // Setup interactions
      setupInteractions(riggedCharRef);

      // Apply action if specified
      if (charData.actionId) {
        playAction(riggedCharRef, charData.actionId);
      }
    } catch (error) {
      console.error('Error setting up rigged character:', error);
    }
  };

  const setupInteractions = (charRef: RiggedCharacterRef) => {
    const { container } = charRef;
    let dragOffset = { x: 0, y: 0 };

    const renderBorder = (ref: RiggedCharacterRef) => {
      // First, remove any existing border or make it visible
      if (ref.borderGraphics) {
        ref.borderGraphics.visible = true;
        return;
      }

      // Calculate sprite bounds (sprite is centered at 0,0 in container)
      // Use scaled components if needed, but in container space, sprite dimensions are what matters
      const width = ref.sprite.width;
      const height = ref.sprite.height;

      // Since sprite anchor is 0.5, 0.5, top-left is at -width/2, -height/2
      const x = -width / 2;
      const y = -height / 2;

      // Create new border graphics
      // Using 0x36cedc to match regular character selection color
      const borderGraphics = drawBorder(x, y, width, height, 0x36cedc);
      ref.borderGraphics = borderGraphics;
      ref.container.addChild(borderGraphics);
    };

    const removeBorder = (ref: RiggedCharacterRef) => {
      if (ref.borderGraphics) {
        ref.borderGraphics.visible = false;
      }
    };

    const selectCharacter = () => {
      // Deselect all others
      riggedCharactersRef.current.forEach((ref) => {
        ref.isSelected = ref.id === charRef.id;
        // Update visual selection feedback
        if (ref.isSelected) {
          onAssetSelection(ref.id, AssetType.RIGGED_CHARACTER);
          renderBorder(ref);
        } else {
          removeBorder(ref);
        }
      });
    };

    container.on('pointerdown', (event) => {
      event.stopPropagation();
      selectCharacter();
      charRef.isDragging = true;

      const globalPos = event.data.global;
      const containerPos = container.getGlobalPosition();
      dragOffset = {
        x: globalPos.x - containerPos.x,
        y: globalPos.y - containerPos.y,
      };

      container.alpha = 0.8;
    });

    container.on('pointermove', (event) => {
      if (charRef.isDragging && container.parent) {
        const newPos = event.data.getLocalPosition(container.parent);
        container.x = newPos.x - dragOffset.x;
        container.y = newPos.y - dragOffset.y;
        charRef.position = { x: container.x, y: container.y };

        // Check if recording is active
        if (
          isRecordingRef?.current &&
          recordingStartTimeRef?.current !== null &&
          selectedMenuRef?.current === 'animations'
        ) {
          // Use startRecording to record path points and draw red circles
          startRecording(
            event,
            riggedCharactersRef as any,
            charRef.id,
            app!,
            dragOffset
          );
        } else {
          onUpdateRiggedCharacter(charRef.id, {
            position: charRef.position,
          });
        }
      }
    });

    const onPointerUp = () => {
      if (charRef.isDragging) {
        charRef.isDragging = false;
        container.alpha = 1;

        // Stop recording if we were recording
        if (
          isRecordingRef?.current &&
          recordingStartTimeRef?.current !== null
        ) {
          const pathPoints = stopRecording(
            riggedCharactersRef as any,
            onUpdateRiggedCharacter
          );
          if (pathPoints && pathPoints.length > 0) {
            onUpdateRiggedCharacter(charRef.id, { pathPoints: pathPoints });
          }
        }
      }
    };

    container.on('pointerup', onPointerUp);
    container.on('pointerupoutside', onPointerUp);

    // Wheel scaling
    container.on('wheel', (event) => {
      event.stopPropagation();
      const scaleDelta = event.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.min(Math.max(charRef.scale * scaleDelta, 0.1), 5);

      container.scale.set(newScale);
      charRef.scale = newScale;

      onUpdateRiggedCharacter(charRef.id, { scale: newScale });

      // Update border scale if necessary (though child of container should scale auto)
      // Actually, since border is child of container, and we scale container, border scales WITH it.
      // We don't need to rebuild border unless we change 'sprite.scale' (internal normalization scale).
      // But sprite.scale is fixed at setup.
      // So no changes needed here.
    });
  };

  // Update z-index based on layers
  useEffect(() => {
    const stage = app?.stage;
    if (layers && stage) {
      riggedCharactersRef.current.forEach((ref) => {
        const matchingLayer = layers.find((layer) => layer.id === ref.id);
        if (matchingLayer) {
          ref.container.zIndex = layers.indexOf(matchingLayer);
          stage.sortChildren();
        }
      });
    }
  }, [layers, app]);

  return null;
};

/**
 * Draw bone structure on the graphics object
 */
function drawBones(
  graphics: Graphics,
  rig: CharacterRig,
  spriteWidth: number,
  spriteHeight: number
) {
  graphics.clear();
  graphics.alpha = 0.3; // Semi-transparent by default

  // Draw bones
  rig.bones.forEach((bone) => {
    const startJoint = rig.joints.find((j) => j.id === bone.startJointId);
    const endJoint = rig.joints.find((j) => j.id === bone.endJointId);

    if (startJoint && endJoint) {
      // Convert normalized positions to sprite-relative positions
      const startX = (startJoint.position.x - 0.5) * spriteWidth;
      const startY = (startJoint.position.y - 0.5) * spriteHeight;
      const endX = (endJoint.position.x - 0.5) * spriteWidth;
      const endY = (endJoint.position.y - 0.5) * spriteHeight;

      // Draw bone line
      graphics.lineStyle(3, 0xffffff, 0.7);
      graphics.moveTo(startX, startY);
      graphics.lineTo(endX, endY);
    }
  });

  // Draw joints
  rig.joints.forEach((joint) => {
    const x = (joint.position.x - 0.5) * spriteWidth;
    const y = (joint.position.y - 0.5) * spriteHeight;

    graphics.beginFill(0xf39c12);
    graphics.drawCircle(x, y, 5);
    graphics.endFill();
  });
}

/**
 * Play an action animation on a rigged character
 * If segment sprites exist, animates individual bone segments
 * Otherwise falls back to container-based animation
 */
export function playAction(charRef: RiggedCharacterRef, actionId: string) {
  const action = ActionRegistry.getActionById(actionId);
  if (!action) {
    console.warn(`Action not found: ${actionId}`);
    return;
  }

  // Kill any existing animation
  if (charRef.timeline) {
    charRef.timeline.kill();
  }

  charRef.currentAction = action;
  charRef.actionId = actionId;
  resetSegmentSprites(charRef);

  const tl = gsap.timeline({
    repeat: 0,
    onComplete: () => {
      resetSegmentSprites(charRef);
      // Reset container
      charRef.container.rotation = 0;
      charRef.container.position.set(charRef.position.x, charRef.position.y);
    },
  });

  // Check if we have segment sprites for bone-based animation
  const hasSegments = charRef.segmentSprites && charRef.segmentSprites.size > 0;

  if (hasSegments) {
    const poseState = { progress: 0 };

    applySegmentActionPose(charRef, action, 0);

    tl.to(poseState, {
      progress: 1,
      duration: action.duration,
      ease: 'none',
      onUpdate: () => {
        applySegmentActionPose(charRef, action, poseState.progress);
      },
    });

    charRef.timeline = tl;
    tl.pause();
    return;
  }

  // Animate each bone
  action.boneAnimations.forEach((boneAnim: any) => {
    const boneName = boneAnim.boneName;
    const segmentSprite = hasSegments ? charRef.segmentSprites?.get(boneName) : null;

    boneAnim.keyframes.forEach((keyframe: any, index: number) => {
      if (index === 0) return;

      const prevKeyframe = boneAnim.keyframes[index - 1];
      const duration = (keyframe.time - prevKeyframe.time) * action.duration;

      if (segmentSprite) {
        // Animate the specific segment sprite
        tl.to(
          segmentSprite,
          {
            rotation: keyframe.rotation * (Math.PI / 180),
            duration,
            ease: 'power1.inOut',
          },
          prevKeyframe.time * action.duration
        );
      } else {
        // Fallback: animate the whole container (scaled down)
        tl.to(
          charRef.container,
          {
            rotation:
              charRef.container.rotation +
              ((keyframe.rotation * Math.PI) / 180) * 0.15,
            x: charRef.position.x + keyframe.translation.x,
            y: charRef.position.y + keyframe.translation.y,
            duration,
            ease: 'power1.inOut',
          },
          prevKeyframe.time * action.duration
        );
      }
    });
  });

  charRef.timeline = tl;
  // Don't auto-play - the global timeline controls playback via riggedControl.update()
  tl.pause();
}

export default RiggedCharacterRenderer;
